const MAX_RECENT_CURRENCIES = 5;

export const DECIMAL_LAYOUT = Object.freeze({
  GERMAN: 'german',
  ENGLISH: 'english',
});

export const DEFAULT_DECIMAL_LAYOUT = DECIMAL_LAYOUT.GERMAN;

const DECIMAL_LAYOUT_LOCALES = Object.freeze({
  [DECIMAL_LAYOUT.GERMAN]: 'de-DE',
  [DECIMAL_LAYOUT.ENGLISH]: 'en-US',
});

export function hasCompletedDashboardTour(preferences) {
  return preferences?.dashboard?.tourCompleted === true;
}

export function buildDashboardTourCompletedPatch() {
  return {
    dashboard: {
      tourCompleted: true,
    },
  };
}

export function getRecentCurrencyCodes(preferences) {
  const codes = preferences?.sendMoney?.recentCurrencies;
  if (!Array.isArray(codes)) return [];
  return codes
    .map((code) => (typeof code === 'string' ? code.trim().toUpperCase() : ''))
    .filter(Boolean);
}

export function buildAddRecentCurrencyPatch(preferences, currencyCode) {
  const normalized = String(currencyCode).trim().toUpperCase();
  if (!normalized) return null;

  const existing = getRecentCurrencyCodes(preferences);
  const next = [normalized, ...existing.filter((code) => code !== normalized)].slice(0, MAX_RECENT_CURRENCIES);

  return {
    sendMoney: {
      recentCurrencies: next,
    },
  };
}

export function normalizeDecimalLayout(value) {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  if (normalized === DECIMAL_LAYOUT.ENGLISH || normalized === 'en' || normalized === 'en-us' || normalized === 'us') {
    return DECIMAL_LAYOUT.ENGLISH;
  }
  if (normalized === DECIMAL_LAYOUT.GERMAN || normalized === 'de' || normalized === 'de-de') {
    return DECIMAL_LAYOUT.GERMAN;
  }
  return DEFAULT_DECIMAL_LAYOUT;
}

export function getDecimalLayout(preferences) {
  return normalizeDecimalLayout(preferences?.display?.decimalLayout);
}

export function getDecimalLayoutLocale(preferencesOrLayout) {
  const layout =
    typeof preferencesOrLayout === 'string'
      ? normalizeDecimalLayout(preferencesOrLayout)
      : getDecimalLayout(preferencesOrLayout);
  return DECIMAL_LAYOUT_LOCALES[layout] || DECIMAL_LAYOUT_LOCALES[DEFAULT_DECIMAL_LAYOUT];
}

export function buildDecimalLayoutPatch(layout) {
  return {
    display: {
      decimalLayout: normalizeDecimalLayout(layout),
    },
  };
}

export function formatNumberWithDecimalLayout(value, preferencesOrLayout, options = {}) {
  const locale = getDecimalLayoutLocale(preferencesOrLayout);
  const amount = Number(value);
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options;

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(safeAmount);
}

export function formatBalanceDisplayParts(amount, preferencesOrLayout) {
  const locale = getDecimalLayoutLocale(preferencesOrLayout);
  const formatted = formatNumberWithDecimalLayout(amount, preferencesOrLayout);
  const decimalSeparator = locale === 'de-DE' ? ',' : '.';
  const separatorIndex = formatted.lastIndexOf(decimalSeparator);
  if (separatorIndex === -1) {
    return { whole: formatted, fraction: `${decimalSeparator}00` };
  }
  return {
    whole: formatted.slice(0, separatorIndex),
    fraction: formatted.slice(separatorIndex),
  };
}

export function parseAmountInputValue(value, preferencesOrLayout) {
  if (value == null || value === '') return 0;
  const str = String(value).trim();
  const layout =
    typeof preferencesOrLayout === 'string'
      ? normalizeDecimalLayout(preferencesOrLayout)
      : getDecimalLayout(preferencesOrLayout);
  if (layout === DECIMAL_LAYOUT.ENGLISH) {
    return Number.parseFloat(str.replace(/,/g, '')) || 0;
  }
  if (str.includes(',')) {
    return Number.parseFloat(str.replace(/\./g, '').replace(',', '.')) || 0;
  }
  return Number.parseFloat(str.replace(/,/g, '')) || 0;
}

export function formatAmountInputValue(value, preferencesOrLayout) {
  if (!value) return '';
  const layout =
    typeof preferencesOrLayout === 'string'
      ? normalizeDecimalLayout(preferencesOrLayout)
      : getDecimalLayout(preferencesOrLayout);
  if (layout === DECIMAL_LAYOUT.ENGLISH) {
    const cleaned = String(value).replace(/[^\d.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length > 2) return `${parts[0]}.${parts.slice(1).join('')}`;
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.length === 2 ? `${integerPart}.${parts[1]}` : integerPart;
  }

  const cleaned = String(value).replace(/[^\d,]/g, '');
  const parts = cleaned.split(',');
  if (parts.length > 2) {
    return `${parts[0].replace(/\./g, '')},${parts.slice(1).join('')}`;
  }
  const integerDigits = parts[0].replace(/\./g, '');
  const integerPart = integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return parts.length === 2 ? `${integerPart},${parts[1]}` : integerPart;
}
